import React, { useEffect } from "react";

import { Switch } from "react-router-dom";
import { SecureRoute } from "@okta/okta-react";
import { useIdleTimer } from "@tailormed/common-client/services/idleTimer";
import { createStructuredSelector } from "reselect";
import { connect, useSelector } from "react-redux";
import { Layout } from "antd";

import { EnvironmentHeader } from "../component/EnvironmentHeader";
import { SearchResults } from "../component/SearchResults";
import { RecentPatients } from "../component/RecentPatients";
import Dashboard from "../screen/Dashboard";
import { Patient } from "../../src/screen/patient/Patient";
import PatientRedirect from "../../src/screen/patient/PatientRedirect";
import { FilterListView } from "../../src/screen/FilterListView";
import { AIChatView } from "../component/AIChat/AIChatView";
import { MainHeader } from "../component/MainHeader";
import ManagePatientDetails from "../screen/ManagePatientDetails";
import { TopOpportunities } from "../component/TopOpportunities";
import { PatientConfirmationAlertContextProvider } from "../context/PatientConfirmationAlertContext";
import { AppLoader } from "../component/AppLoader";
import { PatientReport } from "../screen/patient/PatientReport";
import CoverageConflicts from "../screen/CoverageConflicts";
import { Settings } from "../screen/Settings";
import { handleReturnFromSleep, renewTokenTimer } from "@tailormed/common-client/services/auth";
import { selectUser, selectIDPConfig, selectEnvironment } from "../store/selector";
import { useGetSetting } from "../hooks/getSetting";
import { oktaConfig } from "../constant/okta";
import { ChromeExtensionListener } from "../component/ChromeExtensionListener";
import { OnlineWizard } from "../component/OnlineWizard/OnlineWizard";
import { selectIsDevEnvironment } from "@tailormed/common-client/store/selector";
import { GRAY_50 } from "../constant/colors";
import { SidebarMenu } from "../component/SidebarMenu";
import BulkActionNavGuard from "../component/BulkActions/BulkActionNavGuard";
import { Uploader } from "../component/Uploader";
import { MemoFloatingChatWidget } from "../component/AIChat/FloatingChat/MemoFloatingChatWidget";
import NotificationSettings from "../screen/NotificationSettings";
import { PortalCredentialsPage } from "../screen/PortalCredentials";
import { PhysicianManagement, PhysicianDetail, PhysicianFormPage } from "../screen/PhysicianManagement";
import { FacilityManagement, FacilityDetail, FacilityFormPage } from "../screen/FacilityManagement";
import AutomaticRulesSettings from "../screen/AutomaticRulesSettings";
import { AgenticPlatformTasks, AgenticPlatformDashboard } from "../screen/AgenticPlatform";
import { canAccessAgenticPlatform } from "../constant/agenticPlatform";
import { identifyPendoUser } from "../utils/pendo/pendoUtils";
import { identifyMixpanelUser } from "../utils/mixpanel/mixPanelUtils";

const SecuredRouterComponent = ({ user, idpConfig, isDevEnvironment }) => {
  const account = useSelector(selectEnvironment);
  useIdleTimer(user, idpConfig, oktaConfig.loginURI);

  useEffect(() => {
    const initAuthTimers = async () => {
      await renewTokenTimer(user, idpConfig, oktaConfig.loginURI);
      await handleReturnFromSleep(user, idpConfig, oktaConfig.loginURI);
    };
    initAuthTimers();
  }, []);

  useEffect(() => {
    identifyPendoUser(user, account);
    identifyMixpanelUser(user, account);
  }, [user, account]);

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <AppLoader />
      <MemoFloatingChatWidget />
      <Switch>
        <SecureRoute path={"/patient/:patientId/journey/:journeyId/report"} component={PatientReport} />
        <SecureRoute
          path={"/"}
          render={() => <SecuredRouterWithAppLayouts isDevEnvironment={isDevEnvironment} user={user} />}
        />
      </Switch>
    </Layout>
  );
};

const SecuredRouterWithAppLayouts = ({ isDevEnvironment, user }) => {
  const [isFacilityManagementEnabled] = useGetSetting(["facility-management.enabled"]);

  return (
    <PatientConfirmationAlertContextProvider>
      <EnvironmentHeader />
      <Layout style={{ minHeight: "100vh" }}>
        <BulkActionNavGuard />
        <SidebarMenu />
        <Layout style={{ backgroundColor: GRAY_50 }}>
          <MainHeader />
          <Switch>
            <SecureRoute
              path={"/patient/:patientId/journey/:journeyId/coverages/conflicts"}
              component={CoverageConflicts}
            />
            <SecureRoute path={"/"} component={Dashboard} exact />
            <SecureRoute path={"/patient/:patientId/journey/:journeyId/edit"} component={ManagePatientDetails} exact />
            <SecureRoute path={"/patient/:patientId/journey/:journeyId"} component={Patient} />
            <SecureRoute path={"/patient/:patientId"} component={PatientRedirect} exact />
            <SecureRoute path={"/savedLists/:filterListId"} component={FilterListView} exact />
            <SecureRoute path={"/top-opportunities/:notificationId"} component={TopOpportunities} />
            <SecureRoute path={"/search-results"} component={SearchResults} />
            <SecureRoute path={"/recent-patients"} component={RecentPatients} />
            <SecureRoute path={"/settings"} component={Settings} exact />
            <SecureRoute path={"/ai-chat-bot/conversations/:conversationId/messages"} component={AIChatView} />
            <SecureRoute path={"/ai-chat-bot/new"} component={AIChatView} exact />
            <SecureRoute path={"/notification-preferences"} component={NotificationSettings} exact />
            <SecureRoute path={"/portal-credentials"} component={PortalCredentialsPage} exact />
            <SecureRoute path={"/physicians-settings/new"} component={PhysicianFormPage} exact />
            <SecureRoute path={"/physicians-settings/:id/edit"} component={PhysicianFormPage} exact />
            <SecureRoute path={"/physicians-settings/:id"} component={PhysicianDetail} exact />
            <SecureRoute path={"/physicians-settings"} component={PhysicianManagement} exact />
            {isFacilityManagementEnabled && (
              <SecureRoute path={"/facilities-settings/:id/edit"} component={FacilityFormPage} exact />
            )}
            {isFacilityManagementEnabled && (
              <SecureRoute path={"/facilities-settings/:id"} component={FacilityDetail} exact />
            )}
            {isFacilityManagementEnabled && (
              <SecureRoute path={"/facilities-settings"} component={FacilityManagement} exact />
            )}
            <SecureRoute path={"/automatic-rules"} component={AutomaticRulesSettings} exact />
            {/* Agentic platform: a TMComplete user with an allowed role
              (operations manager / financial navigator / insights). The route
              is also blocked at the menu level (see MenuItems.js); gating the
              route here closes the direct-URL backdoor. */}
            {canAccessAgenticPlatform(user) && (
              <SecureRoute path={"/agentic-platform/tasks"} component={AgenticPlatformTasks} exact />
            )}
            {canAccessAgenticPlatform(user) && (
              <SecureRoute path={"/agentic-platform/dashboard"} component={AgenticPlatformDashboard} exact />
            )}
            {isDevEnvironment && !user?.isExternal && <SecureRoute path={"/online-wizard"} component={OnlineWizard} />}
          </Switch>
          <Uploader />
        </Layout>
      </Layout>
      <ChromeExtensionListener />
    </PatientConfirmationAlertContextProvider>
  );
};

const mapStateToProps = createStructuredSelector({
  user: selectUser,
  idpConfig: selectIDPConfig,
  isDevEnvironment: selectIsDevEnvironment
});

const SecuredRouter = connect(mapStateToProps, null)(SecuredRouterComponent);
export { SecuredRouter };
